import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './PlPage.css';

const fmtKamas = (n) => `${Math.round(n || 0).toLocaleString(getLocale()).replace(/ /g, ' ')} k`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(getLocale(), { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

const SCREEN_MAX_WIDTH = 1920;

/** Réduit le screen (JPEG, 1920 px de large max) avant l'envoi. */
const compressScreen = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const scale = Math.min(1, SCREEN_MAX_WIDTH / img.width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image')); };
    img.src = url;
});

/* ── Screen stocké (chargé avec le jeton, cliquable pour agrandir) ── */
const screenCache = new Map();

const StoredScreen = ({ serverId, screenId, onOpen }) => {
    const [src, setSrc] = useState(screenCache.get(screenId) || '');
    useEffect(() => {
        if (!screenId || screenCache.has(screenId)) return;
        let alive = true;
        api.get(`/pl/${serverId}/screens/${screenId}`, { responseType: 'blob' })
            .then(res => {
                const url = URL.createObjectURL(res.data);
                screenCache.set(screenId, url);
                if (alive) setSrc(url);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [serverId, screenId]);

    if (!src) return <span className="pl-screen pl-screen--loading"><Icon name="image" size={20} /></span>;
    return (
        <button type="button" className="pl-screen" onClick={() => onOpen(src)}>
            <img src={src} alt="" />
        </button>
    );
};

const Lightbox = ({ src, onClose }) => (
    <div className="c2-modal-backdrop pl-lightbox" role="presentation" onClick={onClose}>
        <img src={src} alt="" />
    </div>
);

/* ── Zone d'ajout du screen : fichier, glisser-déposer ou Ctrl+V ── */
const ScreenInput = ({ value, onChange }) => {
    const { t } = useLanguage();
    const { error: showError } = useNotification();

    const take = useCallback(async (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        try {
            onChange(await compressScreen(file));
        } catch {
            showError(t('pl.screenError', 'Impossible de lire cette image'));
        }
    }, [onChange, showError, t]);

    useEffect(() => {
        const onPaste = (e) => {
            const file = [...(e.clipboardData?.files || [])].find(f => f.type.startsWith('image/'));
            if (file) { e.preventDefault(); take(file); }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [take]);

    return (
        <label
            className={`pl-drop${value ? ' has-image' : ''}`}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); take(e.dataTransfer.files?.[0]); }}
        >
            <input type="file" accept="image/*" onChange={e => take(e.target.files?.[0])} hidden />
            {value ? (
                <>
                    <img src={value} alt="" />
                    <button type="button" className="c2-icon-btn c2-icon-btn--danger pl-drop__clear" onClick={e => { e.preventDefault(); onChange(''); }} aria-label={t('pl.removeScreen', 'Retirer le screen')}>
                        <Icon name="close" size={16} />
                    </button>
                </>
            ) : (
                <span className="pl-drop__hint">
                    <Icon name="image" size={24} />
                    {t('pl.screenHint', 'Colle le screen (Ctrl+V), glisse-le ici ou clique pour le choisir')}
                </span>
            )}
        </label>
    );
};

/* ── Liste d'items à cocher, groupée par emplacement ── */
const ItemChecklist = ({ items, selected, onToggle, renderExtra }) => (
    <ul className="pl-checklist">
        {items.map(item => (
            <li key={item._id}>
                <label>
                    <input type="checkbox" className="c2-check" checked={selected.has(item._id)} onChange={() => onToggle(item._id)} />
                    <span className="pl-checklist__slot">{item.slot}</span>
                    <span className="pl-checklist__name">{item.name}</span>
                    {renderExtra?.(item)}
                </label>
            </li>
        ))}
    </ul>
);

/* ── Prêt ou retour d'items ── */
const MovementModal = ({ serverId, type, client, kits, movements, onClose, onSaved, onOpenScreen }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const isOut = type === 'out';
    // Prêt : uniquement des kits complets (rien de sorti). Retour : les kits dont le joueur détient des items
    const kitChoices = isOut
        ? kits.filter(k => k.items.length && !k.items.some(i => i.holder))
        : kits.filter(k => client.held.some(h => h.kitId === k._id));
    const [kitId, setKitId] = useState(kitChoices[0]?._id || '');
    const [selected, setSelected] = useState(new Set());
    const [screen, setScreen] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const kit = kits.find(k => k._id === kitId);
    const items = useMemo(() => {
        if (!kit) return [];
        return isOut ? kit.items : kit.items.filter(i => i.holder?.clientId === client._id);
    }, [kit, isOut, client._id]);

    // Prêt : tout le kit. Retour : tout est coché par défaut, on décoche ce qui manque
    useEffect(() => {
        setSelected(new Set(items.map(i => i._id)));
    }, [kitId]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (id) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const missing = isOut ? [] : items.filter(i => !selected.has(i._id));
    // Screens des prêts précédents de ce kit, pour comparer au retour
    const loanScreens = isOut ? [] : movements.filter(m => m.type === 'out' && m.kitId === kitId && m.screenId).slice(0, 3);

    const save = async () => {
        if (!screen && !window.confirm(t('pl.confirmNoScreen', 'Aucun screen joint. Continuer quand même ?'))) return;
        setSaving(true);
        try {
            await api.post(`/pl/${serverId}/clients/${client._id}/movements`, { type, kitId, itemIds: [...selected], screen, note });
            success(isOut ? t('pl.lent', 'Prêt enregistré') : t('pl.returned', 'Retour enregistré'));
            onSaved();
        } catch (err) {
            showError(errorOf(err, t('pl.saveError', 'Enregistrement impossible')));
            setSaving(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal pl-modal pl-modal--wide" role="dialog" aria-modal="true" aria-labelledby="pl-move-title">
                <div className="c2-modal__head">
                    <h2 id="pl-move-title">
                        {isOut ? t('pl.lendTo', 'Prêter à {name}', { name: client.characterName }) : t('pl.returnFrom', 'Retour de {name}', { name: client.characterName })}
                    </h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} disabled={saving} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>

                {kitChoices.length === 0 ? (
                    <p className="c2-muted">{isOut ? t('pl.noFreeKit', 'Aucun kit complet disponible.') : t('pl.nothingHeld', 'Ce joueur ne détient aucun item.')}</p>
                ) : (
                    <div className="pl-move">
                        <div className="pl-move__items">
                            <label className="c2-field">
                                <span className="c2-field__label">{t('pl.kit', 'Kit')}</span>
                                <select className="c2-select" value={kitId} onChange={e => setKitId(e.target.value)}>
                                    {kitChoices.map(k => <option key={k._id} value={k._id}>{k.name}</option>)}
                                </select>
                            </label>

                            {isOut ? (
                                <p className="c2-muted pl-move__intro">{t('pl.lendIntro', 'Le kit complet ({n} items) est prêté. Le screen sert de preuve.', { n: items.length })}</p>
                            ) : (
                                <>
                                    <p className="c2-muted pl-move__intro">{t('pl.returnIntro', 'Décoche ce qui n\'a pas été rendu : ces items resteront au nom du joueur.')}</p>
                                    <ItemChecklist
                                        items={items}
                                        selected={selected}
                                        onToggle={toggle}
                                        renderExtra={item => (!selected.has(item._id) ? <span className="pl-chip pl-chip--danger">{t('pl.missing', 'Manquant')}</span> : null)}
                                    />
                                </>
                            )}
                        </div>

                        <div className="pl-move__screens">
                            <div className="c2-field">
                                <span className="c2-field__label">{isOut ? t('pl.lendScreen', 'Screen du prêt') : t('pl.returnScreen', 'Screen du retour')}</span>
                                <ScreenInput value={screen} onChange={setScreen} />
                            </div>
                            {loanScreens.length > 0 && (
                                <div className="c2-field">
                                    <span className="c2-field__label">{t('pl.loanScreens', 'Screens au moment du prêt')}</span>
                                    <div className="pl-screens">
                                        {loanScreens.map(m => <StoredScreen key={m._id} serverId={serverId} screenId={m.screenId} onOpen={onOpenScreen} />)}
                                    </div>
                                </div>
                            )}
                            <label className="c2-field">
                                <span className="c2-field__label">{t('pl.note', 'Note')}</span>
                                <input className="c2-input" value={note} onChange={e => setNote(e.target.value)} maxLength={500} />
                            </label>
                        </div>
                    </div>
                )}

                <div className="c2-modal__actions">
                    {missing.length > 0 && <span className="pl-move__missing">{t('pl.missingCount', '{n} item(s) manquant(s)', { n: missing.length })}</span>}
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                    <button type="button" className="c2-btn c2-btn--primary" onClick={save} disabled={saving || selected.size === 0}>
                        <Icon name="check" size={18} />
                        {isOut
                            ? t('pl.lendKit', 'Prêter le kit')
                            : t('pl.returnN', 'Valider le retour de {n} item(s)', { n: selected.size })}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Fiche d'un bénéficiaire (création ou suivi) ── */
const STATUS_LABELS = (t) => ({
    active: t('pl.statusActive', 'En cours'),
    done: t('pl.statusDone', 'Terminé'),
    cancelled: t('pl.statusCancelled', 'Abandonné')
});

const ClientModal = ({ serverId, client, defaults, kits, onClose, onChanged }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const isNew = !client._id;
    const [form, setForm] = useState({
        characterName: client.characterName || '',
        startLevel: client.startLevel || 1,
        price: client.price ?? defaults.price,
        paid: !!client.paid,
        aidAmount: client.aidAmount || 0,
        status: client.status || 'active',
        notes: client.notes || ''
    });
    const [saving, setSaving] = useState(false);
    const [movements, setMovements] = useState([]);
    const [moveType, setMoveType] = useState(null);
    const [lightbox, setLightbox] = useState('');
    // Création : prêt d'un kit complet dans la foulée
    const freeKits = useMemo(() => kits.filter(k => k.items.length && !k.items.some(i => i.holder)), [kits]);
    const [lendNow, setLendNow] = useState(false);
    const [lendKitId, setLendKitId] = useState(freeKits[0]?._id || '');
    const [lendScreen, setLendScreen] = useState('');
    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const statusLabels = STATUS_LABELS(t);

    const loadMovements = useCallback(() => {
        if (isNew) return;
        api.get(`/pl/${serverId}/clients/${client._id}/movements`).then(res => setMovements(res.data)).catch(() => {});
    }, [serverId, client._id, isNew]);
    useEffect(() => { loadMovements(); }, [loadMovements]);

    const save = async (e) => {
        e.preventDefault();
        if (form.status !== 'active' && client.held?.length && form.status !== client.status
            && !window.confirm(t('pl.confirmCloseWithItems', '{name} détient encore {n} item(s). Les marquer comme manquants ?', { name: form.characterName, n: client.held.length }))) return;
        if (isNew && lendNow && !lendScreen && !window.confirm(t('pl.confirmNoScreen', 'Aucun screen joint. Continuer quand même ?'))) return;
        setSaving(true);
        try {
            if (isNew) {
                const res = await api.post(`/pl/${serverId}/clients`, form);
                if (lendNow) {
                    const kit = freeKits.find(k => k._id === lendKitId);
                    try {
                        await api.post(`/pl/${serverId}/clients/${res.data._id}/movements`, { type: 'out', kitId: lendKitId, itemIds: kit.items.map(i => i._id), screen: lendScreen });
                    } catch (err) {
                        showError(t('pl.lendAfterCreateError', 'Fiche créée, mais le prêt a échoué : {error}', { error: errorOf(err, '') }));
                    }
                }
            } else {
                await api.put(`/pl/${serverId}/clients/${client._id}`, form);
            }
            success(t('pl.clientSaved', 'Fiche enregistrée'));
            onChanged(isNew);
        } catch (err) {
            showError(errorOf(err, t('pl.saveError', 'Enregistrement impossible')));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(t('pl.confirmDeleteClient', 'Supprimer la fiche de {name} et son historique ?', { name: client.characterName }))) return;
        try {
            await api.delete(`/pl/${serverId}/clients/${client._id}`);
            success(t('pl.clientDeleted', 'Fiche supprimée'));
            onChanged(true);
        } catch (err) {
            showError(errorOf(err, t('pl.deleteError', 'Suppression impossible')));
        }
    };

    const heldByKit = useMemo(() => {
        const groups = new Map();
        (client.held || []).forEach(h => {
            if (!groups.has(h.kitName)) groups.set(h.kitName, []);
            groups.get(h.kitName).push(h);
        });
        return [...groups.entries()];
    }, [client.held]);
    const isOver = client.status && client.status !== 'active';

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal pl-modal pl-modal--wide" role="dialog" aria-modal="true" aria-labelledby="pl-client-title">
                <div className="c2-modal__head">
                    <h2 id="pl-client-title">{isNew ? t('pl.newClient', 'Nouveau PL') : client.characterName}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>

                <form className="pl-form" onSubmit={save}>
                    <div className="pl-form__grid">
                        <label className="c2-field">
                            <span className="c2-field__label">{t('pl.player', 'Joueur')}</span>
                            <input className="c2-input" value={form.characterName} onChange={e => setField('characterName', e.target.value)} required maxLength={60} />
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('pl.startLevel', 'Niveau de départ')}</span>
                            <input type="number" min="1" max="200" className="c2-input" value={form.startLevel} onChange={e => setField('startLevel', e.target.value)} />
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('pl.status', 'Statut')}</span>
                            <select className="c2-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                                {Object.entries(statusLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </label>
                        <label className="c2-field">
                            <span className="c2-field__label">{t('pl.price', 'Tarif (kamas)')}</span>
                            <input type="number" min="0" className="c2-input" value={form.price} onChange={e => setField('price', e.target.value)} />
                        </label>
                        <div className="c2-field pl-form__checks">
                            {isNew && (
                                <label className="pl-inline-check" title={freeKits.length ? undefined : t('pl.noFreeKit', 'Aucun kit complet disponible.')}>
                                    <input type="checkbox" className="c2-check" checked={lendNow} disabled={!freeKits.length} onChange={e => setLendNow(e.target.checked)} />
                                    {t('pl.lendNow', 'Prêter un kit tout de suite')}
                                </label>
                            )}
                            <label className="pl-inline-check">
                                <input type="checkbox" className="c2-check" checked={form.paid} onChange={e => setField('paid', e.target.checked)} />
                                {t('pl.paid', 'Tarif payé')}
                            </label>
                            <label className="pl-inline-check">
                                <input type="checkbox" className="c2-check" checked={form.aidAmount > 0} onChange={e => setField('aidAmount', e.target.checked ? defaults.aid : 0)} />
                                {t('pl.aid', 'Aide de {amount}', { amount: fmtKamas(defaults.aid) })}
                            </label>
                        </div>
                    </div>
                    {isNew && lendNow && (
                        <div className="pl-form__lend">
                            <label className="c2-field">
                                <span className="c2-field__label">{t('pl.kit', 'Kit')}</span>
                                <select className="c2-select" value={lendKitId} onChange={e => setLendKitId(e.target.value)}>
                                    {freeKits.map(k => <option key={k._id} value={k._id}>{k.name}</option>)}
                                </select>
                            </label>
                            <div className="c2-field">
                                <span className="c2-field__label">{t('pl.lendScreen', 'Screen du prêt')}</span>
                                <ScreenInput value={lendScreen} onChange={setLendScreen} />
                            </div>
                        </div>
                    )}
                    <label className="c2-field">
                        <span className="c2-field__label">{t('pl.notes', 'Notes')}</span>
                        <textarea className="c2-input pl-form__notes" value={form.notes} onChange={e => setField('notes', e.target.value)} maxLength={2000} rows={2} />
                    </label>
                    <div className="c2-row pl-form__actions">
                        {!isNew && (
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm pl-form__delete" onClick={remove}>
                                <Icon name="trash" size={16} />{t('pl.deleteClient', 'Supprimer la fiche')}
                            </button>
                        )}
                        <button type="submit" className="c2-btn c2-btn--primary" disabled={saving}>
                            <Icon name="save" size={18} />
                            {saving ? t('pl.saving', 'Enregistrement...') : (isNew ? t('pl.create', 'Créer') : t('common.save', 'Enregistrer'))}
                        </button>
                    </div>
                </form>

                {!isNew && (
                    <>
                        <section className="pl-held">
                            <div className="c2-section__head">
                                <h3>{t('pl.heldItems', 'Items détenus')}</h3>
                                <div className="c2-row">
                                    <button type="button" className="c2-btn c2-btn--accent c2-btn--sm" onClick={() => setMoveType('out')}>
                                        <Icon name="arrowRight" size={16} />{t('pl.lend', 'Prêter')}
                                    </button>
                                    <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={() => setMoveType('in')} disabled={!client.held.length}>
                                        <Icon name="arrowLeft" size={16} />{t('pl.return', 'Retour')}
                                    </button>
                                </div>
                            </div>
                            {heldByKit.length === 0 ? (
                                <p className="c2-muted">{t('pl.nothingHeld', 'Ce joueur ne détient aucun item.')}</p>
                            ) : heldByKit.map(([kitName, items]) => (
                                <div key={kitName} className="pl-held__kit">
                                    <strong>{kitName}</strong>
                                    <ul>
                                        {items.map(h => (
                                            <li key={h.itemId} className={isOver ? 'is-missing' : ''}>
                                                <span className="pl-checklist__slot">{h.slot}</span>
                                                <span>{h.name}</span>
                                                <span className="c2-muted">{t('pl.since', 'depuis le {date}', { date: fmtDate(h.since) })}</span>
                                                {isOver && <span className="pl-chip pl-chip--danger">{t('pl.missing', 'Manquant')}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </section>

                        <section className="pl-history">
                            <h3>{t('pl.history', 'Historique')}</h3>
                            {movements.length === 0 ? <p className="c2-muted">{t('pl.noHistory', 'Aucun prêt pour le moment.')}</p> : (
                                <ul>
                                    {movements.map(m => (
                                        <li key={m._id} className="pl-history__row">
                                            <span className={`pl-chip ${m.type === 'out' ? 'pl-chip--out' : 'pl-chip--in'}`}>
                                                {m.type === 'out' ? t('pl.loan', 'Prêt') : t('pl.returnShort', 'Retour')}
                                            </span>
                                            <div className="pl-history__body">
                                                <span><strong>{m.kitName}</strong> · {m.items.map(i => i.name).join(', ')}</span>
                                                <span className="c2-muted">{fmtDateTime(m.createdAt)}{m.by ? ` · ${m.by}` : ''}{m.note ? ` · ${m.note}` : ''}</span>
                                            </div>
                                            {m.screenId && <StoredScreen serverId={serverId} screenId={m.screenId} onOpen={setLightbox} />}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </>
                )}
            </div>

            {moveType && (
                <MovementModal
                    serverId={serverId}
                    type={moveType}
                    client={client}
                    kits={kits}
                    movements={movements}
                    onOpenScreen={setLightbox}
                    onClose={() => setMoveType(null)}
                    onSaved={() => { setMoveType(null); loadMovements(); onChanged(false); }}
                />
            )}
            {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox('')} />}
        </div>
    );
};

/* ── Modification d'un kit ── */
const KitModal = ({ serverId, kit, slots, onClose, onSaved }) => {
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [name, setName] = useState(kit.name);
    const [notes, setNotes] = useState(kit.notes || '');
    const [items, setItems] = useState(kit.items.map(i => ({ ...i })));
    const [saving, setSaving] = useState(false);

    const setItem = (index, field, value) => setItems(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    const addItem = () => setItems(prev => [...prev, { slot: slots[0], name: '', level: 0, stages: [] }]);
    const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/pl/${serverId}/kits/${kit._id}`, { name, notes, items });
            success(t('pl.kitSaved', 'Kit enregistré'));
            onSaved();
        } catch (err) {
            showError(errorOf(err, t('pl.saveError', 'Enregistrement impossible')));
            setSaving(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation">
            <div className="c2-card c2-modal pl-modal pl-modal--wide" role="dialog" aria-modal="true" aria-labelledby="pl-kit-title">
                <div className="c2-modal__head">
                    <h2 id="pl-kit-title">{t('pl.editKit', 'Modifier le kit')}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} disabled={saving} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <div className="pl-form__grid">
                    <label className="c2-field">
                        <span className="c2-field__label">{t('pl.kitName', 'Nom du kit')}</span>
                        <input className="c2-input" value={name} onChange={e => setName(e.target.value)} maxLength={60} />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('pl.notes', 'Notes')}</span>
                        <input className="c2-input" value={notes} onChange={e => setNotes(e.target.value)} maxLength={1000} />
                    </label>
                </div>
                <div className="c2-table-wrap">
                    <table className="c2-table pl-kit-table">
                        <thead>
                            <tr>
                                <th>{t('pl.slot', 'Emplacement')}</th>
                                <th>{t('pl.item', 'Item')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={item._id || `new-${index}`}>
                                    <td>
                                        <select className="c2-select c2-input--sm" value={item.slot} onChange={e => setItem(index, 'slot', e.target.value)}>
                                            {[...new Set([...slots, item.slot])].map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </td>
                                    <td><input className="c2-input c2-input--sm" value={item.name} onChange={e => setItem(index, 'name', e.target.value)} maxLength={80} /></td>
                                    <td className="is-num">
                                        {item.holder
                                            ? <span className="pl-chip pl-chip--muted" title={t('pl.atHolder', 'chez {name}', { name: item.holder.characterName })}>{t('pl.lentShort', 'Prêté')}</span>
                                            : <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={() => removeItem(index)} aria-label={t('pl.removeItem', 'Retirer l\'item')}><Icon name="trash" size={16} /></button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="c2-modal__actions">
                    <button type="button" className="c2-btn c2-btn--ghost pl-kit-add" onClick={addItem}><Icon name="plus" size={16} />{t('pl.addItem', 'Ajouter un item')}</button>
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                    <button type="button" className="c2-btn c2-btn--primary" onClick={save} disabled={saving}>
                        <Icon name="save" size={18} />{t('common.save', 'Enregistrer')}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Carte d'un kit ── */
const KitCard = ({ kit, onEdit, onDelete }) => {
    const { t } = useLanguage();
    const lent = kit.items.filter(i => i.holder);
    const byHolder = new Map();
    lent.forEach(i => {
        if (!byHolder.has(i.holder.characterName)) byHolder.set(i.holder.characterName, []);
        byHolder.get(i.holder.characterName).push(i.name);
    });
    return (
        <article className="c2-tile pl-kit">
            <div className="pl-kit__head">
                <strong>{kit.name}</strong>
                {lent.length === 0
                    ? <span className="c2-chip c2-chip--green">{t('pl.available', 'Disponible')}</span>
                    : <span className="c2-chip c2-chip--purple">{t('pl.lentCount', '{n}/{total} prêtés', { n: lent.length, total: kit.items.length })}</span>}
            </div>
            {byHolder.size === 0 ? (
                <p className="c2-muted">{t('pl.kitAllHere', '{n} items, tous rangés', { n: kit.items.length })}</p>
            ) : (
                <ul className="pl-kit__holders">
                    {[...byHolder.entries()].map(([holder, names]) => (
                        <li key={holder}><strong>{holder}</strong> <span className="c2-muted">{names.join(', ')}</span></li>
                    ))}
                </ul>
            )}
            <div className="c2-row">
                <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={onEdit}><Icon name="pencil" size={16} />{t('pl.edit', 'Modifier')}</button>
                {lent.length === 0 && (
                    <button type="button" className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger" onClick={onDelete} aria-label={t('pl.deleteKit', 'Supprimer le kit')} title={t('pl.deleteKit', 'Supprimer le kit')}>
                        <Icon name="trash" size={16} />
                    </button>
                )}
            </div>
        </article>
    );
};

const FILTERS = ['active', 'done', 'all'];

const PlPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('active');
    const [openClientId, setOpenClientId] = useState(null);   // 'new' pour une création
    const [editKitId, setEditKitId] = useState(null);
    const statusLabels = STATUS_LABELS(t);

    const load = useCallback(() => {
        setLoading(true);
        return api.get(`/pl/${serverId}`)
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [serverId]);
    useEffect(() => { load(); }, [load]);

    const createKit = async () => {
        try {
            await api.post(`/pl/${serverId}/kits`);
            success(t('pl.kitCreated', 'Kit créé avec la panoplie sagesse par défaut'));
            load();
        } catch (err) {
            showError(errorOf(err, t('pl.saveError', 'Enregistrement impossible')));
        }
    };

    const deleteKit = async (kit) => {
        if (!window.confirm(t('pl.confirmDeleteKit', 'Supprimer « {name} » ?', { name: kit.name }))) return;
        try {
            await api.delete(`/pl/${serverId}/kits/${kit._id}`);
            load();
        } catch (err) {
            showError(errorOf(err, t('pl.deleteError', 'Suppression impossible')));
        }
    };

    const clients = useMemo(() => data?.clients || [], [data]);
    const visibleClients = useMemo(() => {
        if (filter === 'active') return clients.filter(c => c.status === 'active');
        if (filter === 'done') return clients.filter(c => c.status !== 'active');
        return clients;
    }, [clients, filter]);

    const kpis = useMemo(() => ({
        active: clients.filter(c => c.status === 'active').length,
        collected: clients.filter(c => c.paid).reduce((acc, c) => acc + c.price, 0),
        unpaid: clients.filter(c => !c.paid && c.status !== 'cancelled').reduce((acc, c) => acc + c.price, 0),
        aid: clients.reduce((acc, c) => acc + (c.aidAmount || 0), 0),
        lent: clients.reduce((acc, c) => acc + c.held.length, 0),
        missing: clients.filter(c => c.status !== 'active').reduce((acc, c) => acc + c.held.length, 0)
    }), [clients]);

    const openClient = openClientId === 'new' ? {} : clients.find(c => c._id === openClientId);
    const editKit = data?.kits.find(k => k._id === editKitId);

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="backpack"
                title={t('navigation.pl', 'Service PL')}
                subtitle={t('pl.subtitle', 'Suivi des bénéficiaires et des panoplies sagesse prêtées')}
                actions={
                    <>
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={load} disabled={loading}>
                            <Icon name="refresh" size={18} />{t('common.refresh', 'Actualiser')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={() => setOpenClientId('new')} disabled={!data}>
                            <Icon name="plus" size={18} />{t('pl.newClient', 'Nouveau PL')}
                        </button>
                    </>
                }
            />

            {loading && !data ? (
                <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
            ) : !data ? (
                <p className="c2-empty">{t('pl.loadError', 'Impossible de charger le service PL.')}</p>
            ) : (
                <>
                    <div className="c2-kpis">
                        <div className="c2-kpi"><span className="c2-kpi__value">{kpis.active}</span><span className="c2-kpi__label">{t('pl.kpiActive', 'PL en cours')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{fmtKamas(kpis.collected)}</span><span className="c2-kpi__label">{t('pl.kpiCollected', 'Encaissé')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{fmtKamas(kpis.unpaid)}</span><span className="c2-kpi__label">{t('pl.kpiUnpaid', 'Reste à encaisser')}</span></div>
                        <div className="c2-kpi"><span className="c2-kpi__value">{fmtKamas(kpis.aid)}</span><span className="c2-kpi__label">{t('pl.kpiAid', 'Aides accordées')}</span></div>
                        <div className="c2-kpi">
                            <span className={`c2-kpi__value${kpis.missing ? ' pl-danger' : ''}`}>{kpis.lent}</span>
                            <span className="c2-kpi__label">
                                {kpis.missing ? t('pl.kpiLentMissing', 'Items dehors, dont {n} manquant(s)', { n: kpis.missing }) : t('pl.kpiLent', 'Items prêtés')}
                            </span>
                        </div>
                    </div>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="backpack" size={20} />{t('pl.kits', 'Panoplies sagesse')}</h2>
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={createKit}><Icon name="plus" size={16} />{t('pl.addKit', 'Ajouter un kit')}</button>
                        </div>
                        {data.kits.length === 0 ? (
                            <p className="c2-muted">{t('pl.noKits', 'Aucun kit. Ajoute un kit : il est prérempli avec la panoplie sagesse.')}</p>
                        ) : (
                            <div className="pl-kits">
                                {data.kits.map(kit => <KitCard key={kit._id} kit={kit} onEdit={() => setEditKitId(kit._id)} onDelete={() => deleteKit(kit)} />)}
                            </div>
                        )}
                    </section>

                    <section className="c2-card c2-section">
                        <div className="c2-section__head">
                            <h2 className="c2-section__title"><Icon name="user" size={20} />{t('pl.clients', 'Bénéficiaires')}</h2>
                            <div className="pl-filters" role="tablist">
                                {FILTERS.map(f => (
                                    <button key={f} type="button" role="tab" aria-selected={filter === f} className={`c2-btn c2-btn--sm ${filter === f ? 'c2-btn--accent' : 'c2-btn--ghost'}`} onClick={() => setFilter(f)}>
                                        {f === 'active' ? t('pl.statusActive', 'En cours') : f === 'done' ? t('pl.filterOver', 'Terminés') : t('pl.filterAll', 'Tous')}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {visibleClients.length === 0 ? (
                            <p className="c2-muted">{t('pl.noClients', 'Aucun bénéficiaire.')}</p>
                        ) : (
                            <div className="c2-table-wrap">
                                <table className="c2-table">
                                    <thead>
                                        <tr>
                                            <th>{t('pl.player', 'Joueur')}</th>
                                            <th>{t('pl.startedOn', 'Début')}</th>
                                            <th className="is-num">{t('pl.priceShort', 'Tarif')}</th>
                                            <th className="is-center">{t('pl.aidShort', 'Aide')}</th>
                                            <th className="is-center">{t('pl.items', 'Items')}</th>
                                            <th className="is-center">{t('pl.status', 'Statut')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleClients.map(c => {
                                            const missing = c.status !== 'active' && c.held.length > 0;
                                            return (
                                                <tr key={c._id}>
                                                    <td>
                                                        <strong>{c.characterName}</strong>
                                                    </td>
                                                    <td className="c2-muted">{fmtDate(c.createdAt)} · {t('pl.levelN', 'niv. {n}', { n: c.startLevel })}</td>
                                                    <td className="is-num">
                                                        {fmtKamas(c.price)}{' '}
                                                        <span className={`c2-chip${c.paid ? ' c2-chip--green' : ''}`}>{c.paid ? t('pl.paidShort', 'Payé') : t('pl.unpaidShort', 'À payer')}</span>
                                                    </td>
                                                    <td className="is-center">{c.aidAmount > 0 ? fmtKamas(c.aidAmount) : '—'}</td>
                                                    <td className="is-center">
                                                        {c.held.length === 0 ? <span className="c2-muted">0</span>
                                                            : <span className={`pl-chip ${missing ? 'pl-chip--danger' : 'pl-chip--out'}`}>{missing ? t('pl.missingN', '{n} manquant(s)', { n: c.held.length }) : c.held.length}</span>}
                                                    </td>
                                                    <td className="is-center"><span className={`c2-chip${c.status === 'active' ? ' c2-chip--purple' : ''}`}>{statusLabels[c.status]}</span></td>
                                                    <td className="is-num">
                                                        <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setOpenClientId(c._id)}>{t('pl.open', 'Ouvrir')}</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}

            {openClient && (
                <ClientModal
                    key={openClientId}
                    serverId={serverId}
                    client={openClient}
                    defaults={data.defaults}
                    kits={data.kits}
                    onClose={() => setOpenClientId(null)}
                    onChanged={(close) => { load(); if (close) setOpenClientId(null); }}
                />
            )}
            {editKit && (
                <KitModal
                    serverId={serverId}
                    kit={editKit}
                    slots={data.slots}
                    onClose={() => setEditKitId(null)}
                    onSaved={() => { setEditKitId(null); load(); }}
                />
            )}
        </div>
    );
};

export default PlPage;
